import { API_BASE_URL } from '../config';

export type DatasetExportStatus =
  | 'queued'
  | 'resolving'
  | 'building'
  | 'indexing'
  | 'signing'
  | 'completed'
  | 'completed_with_gaps'
  | 'failed';

export interface DatasetExportProgress {
  sessions_total: number;
  built: number;
  failed: number;
  skipped_videos?: number;
  skipped_events?: number;
  by_format: Record<string, number>;
}

export interface DatasetExportResponse {
  export_id: string;
  dataset_id?: string | null;
  status: DatasetExportStatus;
  progress: DatasetExportProgress;
  requested_video_count: number;
  requested_event_count: number;
  created_at?: string | null;
  updated_at?: string | null;
  error?: string | null;
  manifest_url?: string | null;
  export_gs_uri?: string | null;
  urls_expire_at?: string | null;
}

export interface DatasetExportHistoryResponse {
  items: DatasetExportResponse[];
  next_cursor?: string | null;
}

async function exportRequest(
  path: string,
  token: string,
  init?: RequestInit,
): Promise<DatasetExportResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Dataset export request failed (${response.status}): ${detail}`);
  }
  return response.json();
}

export function createDatasetExport(
  token: string,
  datasetId: string,
  videoIds: string[],
  eventIds: string[],
): Promise<DatasetExportResponse> {
  return exportRequest('/exports/dataset', token, {
    method: 'POST',
    body: JSON.stringify({
      dataset_id: datasetId,
      video_ids: videoIds,
      event_ids: eventIds,
    }),
  });
}

export async function fetchDatasetExportHistory(
  token: string,
  datasetId: string,
  options: { cursor?: string | null; limit?: number } = {},
): Promise<DatasetExportHistoryResponse> {
  const params = new URLSearchParams({
    dataset_id: datasetId,
    limit: String(options.limit ?? 10),
  });
  if (options.cursor) params.set('cursor', options.cursor);
  const response = await fetch(`${API_BASE_URL}/exports/dataset?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Dataset export history request failed (${response.status}): ${detail}`);
  }
  return response.json();
}

export function fetchDatasetExport(
  token: string,
  exportId: string,
): Promise<DatasetExportResponse> {
  return exportRequest(`/exports/dataset/${encodeURIComponent(exportId)}`, token);
}

export function refreshDatasetExportUrls(
  token: string,
  exportId: string,
): Promise<DatasetExportResponse> {
  return exportRequest(
    `/exports/dataset/${encodeURIComponent(exportId)}/refresh-urls`,
    token,
    { method: 'POST' },
  );
}
